import { useMutation } from '@tanstack/react-query';

import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import useEditableQuestion from '@/data/hook/question/useEditableQuestion';
import {
  createQuizQuestionIntoActivity,
  updateQuizQuestionIntoActivity,
} from '@/data/services/question/quizQuestionServices';
import Activity from '@/models/Activity';
import { QuizQuestion } from '@/models/Question';
import { QuizAnswerTypeEnum } from '@/models/Answer';
import { getQuestionErrorMessage } from '@/utils/questions/getQuestionErrorMessage';
import { adapterQuizAnswers } from '@/utils/questions/adapterAnswersDTO';
import {
  adapterFeedbacks,
  sanitizeFeedbacks,
} from '@/utils/questions/adapterFeedbacks';
import { sanitizeQuizAnswers } from '@/utils/questions/sanitizeAnswers';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import alert from '@/utils/shared/alert';
import { isEqual } from 'lodash';
import QuizForm, { QuizFormFields } from './QuizForm';

type QuizFormServiceProps = {
  activity: Activity;
  question: QuizQuestion;
  isActive?: boolean;
};

export default function QuizFormService({
  activity,
  question,
  isActive,
}: QuizFormServiceProps) {
  const gradeCtx = useQuestionExamGradeCtx();
  const { ensureEditable, resolveEditable } = useEditableQuestion(activity);

  const handleCreate = async (data: QuizFormFields): Promise<QuizQuestion> => {
    const { maxGrade, ...rest } = data;
    const editableActivity = await ensureEditable();
    const saved = await createQuizQuestionIntoActivity(editableActivity.id, {
      type: question.type,
      identifier: question.identifier,
      order: question.order,
      answerType: QuizAnswerTypeEnum.TEXT,
      ...rest,
      quizAnswers: sanitizeQuizAnswers(rest.quizAnswers),
      feedbacks: sanitizeFeedbacks(rest.feedbacks),
    });
    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const handleUpdate = async (data: QuizFormFields): Promise<QuizQuestion> => {
    const { maxGrade, ...rest } = data;
    const oldValues: Omit<QuizFormFields, 'maxGrade'> = {
      title: normalizeTranslatableField(question.title),
      context: normalizeTranslatableField(question.context),
      quizAnswers: adapterQuizAnswers(question.quizAnswers ?? []),
      feedbacks: adapterFeedbacks(question.feedbacks ?? []),
    };

    const changedFields: Partial<Omit<QuizFormFields, 'maxGrade'>> = {};
    if (!isEqual(oldValues.title, rest.title)) {
      changedFields.title = rest.title;
    }
    if (!isEqual(oldValues.context, rest.context)) {
      changedFields.context = rest.context;
    }

    const newSanitizedAnswers = sanitizeQuizAnswers(rest.quizAnswers);
    const oldSanitizedAnswers = sanitizeQuizAnswers(oldValues.quizAnswers);
    if (!isEqual(oldSanitizedAnswers, newSanitizedAnswers)) {
      changedFields.quizAnswers = newSanitizedAnswers;
    }

    const newSanitizedFeedbacks = sanitizeFeedbacks(rest.feedbacks);
    const oldSanitizedFeedbacks = sanitizeFeedbacks(oldValues.feedbacks);
    if (!isEqual(oldSanitizedFeedbacks, newSanitizedFeedbacks)) {
      changedFields.feedbacks = newSanitizedFeedbacks;
    }

    const { toClone, updateTarget } = await resolveEditable(question);
    if (changedFields.quizAnswers)
      changedFields.quizAnswers = toClone(
        changedFields.quizAnswers,
        q => q.quizAnswers,
      );
    const saved = await updateTarget(
      changedFields,
      updateQuizQuestionIntoActivity,
    );

    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: (data: QuizFormFields) =>
      !question.id ? handleCreate(data) : handleUpdate(data),
    onError: err => alert.error(getQuestionErrorMessage(err)),
  });

  return (
    <QuizForm
      key={question.identifier}
      isActive={isActive}
      question={question}
      onSubmit={submitQuestion}
      isLoading={isPending}
    />
  );
}
